import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Building2, FlaskConical, Factory, Network, Timer, EyeOff, Headset } from "lucide-react";
import MarketingLayout from "../components/marketing/MarketingLayout";
import { LineMask, FadeIn, SectionTag } from "../components/marketing/Reveal";

const AUDIENCES = [
  { icon: Factory, t: "Aligner companies" },
  { icon: FlaskConical, t: "Dental labs" },
  { icon: Network, t: "DSOs & dental chains" },
  { icon: Building2, t: "Multi-location practices" },
];

const PILLARS = [
  { icon: Timer, t: "Volume, planned", d: "100, 500 or 1,000+ cases per month — a planning operation that scales with your pipeline, without hiring, training or software licensing on your side.", k: "100 / 500 / 1,000+ cases" },
  { icon: Timer, t: "SLA commitments", d: "Defined turnaround commitments per case type, measured and reported. Your production and patient promises stay predictable.", k: "Contractual turnaround" },
  { icon: EyeOff, t: "White label", d: "Your customers never need to know OrthoPlaniX is behind the workflow. Plans, communication and documentation carry your brand.", k: "Fully invisible partner" },
  { icon: Headset, t: "Dedicated support", d: "Named account management, structured escalation and quarterly planning audits that keep quality consistent as volume grows.", k: "Account team + audits" },
];

export default function Enterprise() {
  return (
    <MarketingLayout>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
        <section className="pt-40 pb-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <SectionTag>Enterprise</SectionTag>
            <h1 className="font-display font-black tracking-tight leading-[0.98] text-[clamp(2.6rem,6.5vw,5.5rem)] max-w-4xl">
              <LineMask delay={0.1}>Enterprise orthodontic</LineMask>
              <LineMask delay={0.25} className="text-neon">planning infrastructure.</LineMask>
            </h1>
            <FadeIn delay={0.5}>
              <p className="mt-8 max-w-2xl text-base md:text-lg text-slate-400 leading-relaxed">
                The clinical planning capacity your organization doesn't have to build in-house. OrthoPlaniX operates as your treatment-planning department — at whatever scale your business runs.
              </p>
            </FadeIn>
            <FadeIn delay={0.7}>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                {AUDIENCES.map((a) => (
                  <span key={a.t} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-4 py-2 text-xs font-semibold text-slate-300">
                    <a.icon className="h-4 w-4 text-neon" /> {a.t}
                  </span>
                ))}
              </div>
            </FadeIn>
          </div>
        </section>

        <section className="py-24 lg:py-32 bg-ink-2/40">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid md:grid-cols-2 gap-4 lg:gap-6">
              {PILLARS.map((p, i) => (
                <FadeIn key={p.t} delay={i * 0.08} className="h-full">
                  <div className="h-full bg-white/5 border border-white/10 rounded-2xl p-8 lg:p-10 transition-colors duration-500 hover:border-neon/40" data-testid={`enterprise-pillar-${i}`}>
                    <div className="flex items-center justify-between">
                      <p.icon className="h-7 w-7 text-neon" />
                      <span className="rounded-full bg-neon/10 border border-neon/30 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-neon">{p.k}</span>
                    </div>
                    <p className="font-display font-bold text-2xl mt-6">{p.t}</p>
                    <p className="text-sm text-slate-400 mt-3 leading-relaxed">{p.d}</p>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        <section className="py-24 lg:py-32">
          <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
            <FadeIn>
              <h2 className="font-display font-black tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
                Custom volume pricing.<br /><span className="text-neon">Built around your case mix.</span>
              </h2>
            </FadeIn>
            <FadeIn delay={0.1}>
              <p className="mt-6 text-slate-400 max-w-xl mx-auto text-sm md:text-base">
                Tell us your monthly volume, case complexity mix and turnaround requirements — we'll come back with a structured proposal and pilot plan.
              </p>
            </FadeIn>
            <FadeIn delay={0.2}>
              <Link to="/contact" data-testid="enterprise-cta"
                className="group mt-10 inline-flex items-center gap-2 rounded-full bg-neon px-8 py-4 text-sm font-bold text-ink transition-transform duration-300 hover:scale-105 hover:shadow-[0_0_50px_rgba(0,229,255,0.5)]">
                Talk to the enterprise team <ArrowUpRight className="h-4 w-4" />
              </Link>
            </FadeIn>
          </div>
        </section>
      </motion.div>
    </MarketingLayout>
  );
}
